<template>
  <div class="tags-filter-widget">
    <div class="card h-100">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-tags"></i>
          Теги
        </h6>
      </div>
      <div class="card-body">
        
        <!-- Поиск по тегам -->
        <div class="tags-search mb-3" v-if="safeTags && safeTags.length > 0">
          <div class="input-group input-group-sm">
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
            <input
              ref="searchInput"
              type="text"
              class="form-control"
              placeholder="Поиск тегов..."
              v-model="searchQuery"
              autocomplete="off"
              @keydown.escape="clearSearch"
            />
            <button 
              v-if="searchQuery"
              class="btn btn-outline-secondary"
              type="button"
              @click="clearSearch"
              title="Очистить поиск"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
          
          <!-- Результаты поиска -->
          <div v-if="searchQuery && filteredTags.length === 0" class="search-no-results mt-2">
            <small class="text-muted">
              <i class="bi bi-info-circle"></i>
              Теги не найдены по запросу "{{ searchQuery }}"
            </small>
          </div>
          
          <div v-if="searchQuery && filteredTags.length > 0" class="search-results-info mt-2">
            <small class="text-muted">
              Найдено: {{ filteredTags.length }} {{ getTagsWordForm(filteredTags.length) }}
            </small>
          </div>
        </div>

        <!-- Список тегов -->
        <div v-if="filteredTags.length > 0" class="tags-list">
          <div class="tags-container">
            <div
              v-for="tag in displayedTags"
              :key="tag.id"
              class="form-check tag-item"
              :class="{ 'selected': isTagSelected(tag.id) }"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="`tag-${tag.id}`"
                :value="tag.id"
                :checked="isTagSelected(tag.id)"
                @change="onTagToggle(tag.id)"
              />
              <label class="form-check-label" :for="`tag-${tag.id}`">
                <span 
                  class="tag-color-indicator"
                  :style="{ backgroundColor: tag.color || '#007bff' }"
                ></span>
                <span v-html="highlightSearchText(tag.tag_name)"></span>
                <span class="badge bg-light text-muted ms-1" v-if="tag.materials_count">
                  {{ tag.materials_count }}
                </span>
              </label>
            </div>
          </div>

          <!-- Показать больше/меньше -->
          <div v-if="filteredTags.length > maxDisplayedTags" class="show-more-container mt-2">
            <button
              class="btn btn-link btn-sm text-decoration-none p-0"
              @click="toggleShowAll"
            >
              <i :class="showAll ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              {{ showAll ? 'Показать меньше' : `Показать еще ${filteredTags.length - maxDisplayedTags} тегов` }}
            </button>
          </div>
        </div>

        <!-- Выбранные теги -->
        <div v-if="selectedTagsDetails.length > 0" class="selected-tags mt-3 pt-3 border-top">
          <label class="form-label small">Выбрано:</label>
          <div class="selected-tags-list">
            <span
              v-for="tag in selectedTagsDetails"
              :key="tag.id"
              class="badge selected-tag-badge"
              :style="{ backgroundColor: tag.color || '#007bff' }"
            >
              {{ tag.tag_name }}
              <button
                class="btn-close btn-close-white ms-1"
                @click="removeTag(tag.id)"
                :aria-label="`Удалить тег ${tag.tag_name}`"
              ></button>
            </span>
          </div>
        </div>

        <!-- Статистика и действия -->
        <div class="stats-summary mt-3 pt-3 border-top">
          <small class="text-muted">
            <div v-if="internalSelectedTags.length > 0">
              Выбрано тегов: {{ internalSelectedTags.length }}
            </div>
            <div v-else>
              Доступно тегов: {{ safeTags ? safeTags.length : 0 }}
            </div>
          </small>
          
          <!-- Добавить тег -->
          <div class="add-tag mt-2">
            <button class="btn btn-link btn-sm text-decoration-none p-0" @click="showCreateModal">
              <i class="bi bi-plus-circle"></i>
              Создать тег
            </button>
          </div>
        </div>

        <!-- Пустое состояние -->
        <div v-if="!safeTags || safeTags.length === 0" class="text-center py-3">
          <i class="bi bi-tag text-muted"></i>
          <div class="text-muted small mt-1">Теги не найдены</div>
          <button class="btn btn-link btn-sm" @click="showCreateModal">
            Создать первый тег
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, nextTick } from 'vue';

export default {
  name: 'TagsFilterWidget',
  props: {
    tags: {
      type: Array,
      default: () => []
    },
    selectedTags: {
      type: Array,
      default: () => []
    }
  },
  emits: ['tags-selected', 'create-tag'],
  setup(props, { emit }) {
    
    // Локальное состояние
    const searchQuery = ref('');
    const showAll = ref(false);
    const maxDisplayedTags = 8;
    const internalSelectedTags = ref([...(props.selectedTags || [])]);
    const searchInput = ref(null);
    
    // Безопасный доступ к tags для template
    const safeTags = computed(() => props.tags || []);

    // Computed свойства
    const filteredTags = computed(() => {
      const tagsArray = props.tags || [];
      if (!tagsArray || !Array.isArray(tagsArray)) return [];
      
      if (!searchQuery.value) {
        return [...tagsArray].sort((a, b) => (b.materials_count || 0) - (a.materials_count || 0));
      }
      
      const query = searchQuery.value.toLowerCase().trim();
      if (!query) {
        return [...tagsArray].sort((a, b) => (b.materials_count || 0) - (a.materials_count || 0));
      }
      
      // Более умная фильтрация
      const filtered = tagsArray.filter(tag => {
        const tagName = tag.tag_name.toLowerCase();
        const description = (tag.description || '').toLowerCase();
        
        // Точное совпадение в начале имеет высший приоритет
        if (tagName.startsWith(query)) return true;
        
        // Поиск по словам
        if (tagName.includes(query)) return true;
        
        // Поиск в описании (если есть)
        if (description.includes(query)) return true;
        
        return false;
      });
      
      // Сортировка результатов поиска: сначала точные совпадения, потом по количеству материалов
      return filtered.sort((a, b) => {
        const aName = a.tag_name.toLowerCase();
        const bName = b.tag_name.toLowerCase();
        
        // Точное совпадение в начале - наивысший приоритет
        const aStartsWith = aName.startsWith(query);
        const bStartsWith = bName.startsWith(query);
        
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        
        // Если оба начинаются с поиска или оба не начинаются - сортировка по количеству материалов
        return (b.materials_count || 0) - (a.materials_count || 0);
      });
    });

    const displayedTags = computed(() => {
      if (showAll.value || filteredTags.value.length <= maxDisplayedTags) {
        return filteredTags.value;
      }
      return filteredTags.value.slice(0, maxDisplayedTags);
    });

    const selectedTagsDetails = computed(() => {
      const tagsArray = props.tags || [];
      if (!tagsArray || !Array.isArray(tagsArray)) return [];
      
      return internalSelectedTags.value
        .map(tagId => tagsArray.find(tag => tag.id === tagId))
        .filter(tag => tag);
    });

    // Методы
    const isTagSelected = (tagId) => {
      return internalSelectedTags.value.includes(tagId);
    };

    const onTagToggle = (tagId) => {
      const index = internalSelectedTags.value.indexOf(tagId);
      
      if (index > -1) {
        // Убираем тег
        internalSelectedTags.value.splice(index, 1);
      } else {
        // Добавляем тег
        internalSelectedTags.value.push(tagId);
      }
      
      emit('tags-selected', [...internalSelectedTags.value]);
    };

    const removeTag = (tagId) => {
      const index = internalSelectedTags.value.indexOf(tagId);
      if (index > -1) {
        internalSelectedTags.value.splice(index, 1);
        emit('tags-selected', [...internalSelectedTags.value]);
      }
    };

    const toggleShowAll = () => {
      showAll.value = !showAll.value;
    };

    const showCreateModal = () => {
      emit('create-tag');
    };

    const clearSearch = () => {
      searchQuery.value = '';
    };

    const getTagsWordForm = (count) => {
      if (count % 10 === 1 && count % 100 !== 11) {
        return 'тег';
      } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
        return 'тега';
      } else {
        return 'тегов';
      }
    };

    const highlightSearchText = (text) => {
      if (!searchQuery.value || !text) {
        return text;
      }
      
      const query = searchQuery.value.toLowerCase().trim();
      if (!query) {
        return text;
      }
      
      // Экранирование HTML и подсветка
      const escapeHtml = (unsafe) => {
        return unsafe
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };
      
      const escapedText = escapeHtml(text);
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      
      return escapedText.replace(regex, '<mark class="search-highlight">$1</mark>');
    };

    // Lifecycle hooks
    onMounted(() => {
      // Фокус на поле поиска при наличии большого количества тегов
      nextTick(() => {
        if (props.tags && props.tags.length > 10 && searchInput.value) {
          searchInput.value.focus();
        }
      });
    });

    // Синхронизация с родительскими пропсами
    watch(() => props.selectedTags, (newTags) => {
      internalSelectedTags.value = [...(newTags || [])];
    }, { deep: true });

    return {
      safeTags,
      searchQuery,
      searchInput,
      showAll,
      maxDisplayedTags,
      internalSelectedTags,
      filteredTags,
      displayedTags,
      selectedTagsDetails,
      isTagSelected,
      onTagToggle,
      removeTag,
      toggleShowAll,
      showCreateModal,
      clearSearch,
      getTagsWordForm,
      highlightSearchText
    };
  }
};
</script>

<style scoped>
.tags-filter-widget .card {
  border: 1px solid var(--yt-border-color);
}

.card-header {
  background-color: var(--yt-bg-secondary);
  border-bottom: 1px solid var(--yt-border-color);
}

.tags-container {
  max-height: 200px;
  overflow-y: auto;
}

.tag-item {
  padding: 4px 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.tag-item:hover {
  background-color: var(--yt-bg-secondary);
}

.tag-item.selected {
  background-color: var(--yt-primary-light);
}

.tag-color-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.form-check-label {
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.selected-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.selected-tag-badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  color: var(--yt-bg-primary);
}

.selected-tag-badge .btn-close {
  font-size: 0.6rem;
  opacity: 0.8;
}

.selected-tag-badge .btn-close:hover {
  opacity: 1;
}

.show-more-container {
  text-align: center;
  border-top: 1px solid var(--yt-bg-tertiary);
  padding-top: 8px;
}

.show-more-container .btn {
  font-size: 0.8rem;
  color: var(--yt-text-secondary);
}

.show-more-container .btn:hover {
  color: var(--yt-primary);
}

.stats-summary {
  font-size: 0.8rem;
}

.add-tag {
  border-top: 1px solid var(--yt-bg-tertiary);
  padding-top: 10px;
  margin-top: 10px;
}

.add-tag .btn {
  font-size: 0.8rem;
  color: var(--yt-text-secondary);
}

.add-tag .btn:hover {
  color: var(--yt-primary);
}

.tags-search .form-control {
  font-size: 0.8rem;
  border-right: none;
}

.tags-search .input-group-text {
  background-color: var(--yt-bg-secondary);
  border-right: none;
  color: var(--yt-text-secondary);
  font-size: 0.8rem;
}

.tags-search .btn-outline-secondary {
  border-left: none;
  font-size: 0.8rem;
}

.search-no-results {
  padding: 8px 12px;
  background-color: var(--yt-warning);
  border: 1px solid var(--yt-warning);
  border-radius: 4px;
  color: var(--yt-text-primary);
  opacity: 0.9;
}

.search-results-info {
  color: var(--yt-text-secondary);
}

.search-highlight {
  background-color: var(--yt-warning);
  color: var(--yt-text-primary);
  padding: 1px 2px;
  border-radius: 2px;
  font-weight: 500;
}

/* Кастомный scrollbar для списка тегов */
.tags-container::-webkit-scrollbar {
  width: 4px;
}

.tags-container::-webkit-scrollbar-track {
  background: var(--yt-bg-secondary);
  border-radius: 2px;
}

.tags-container::-webkit-scrollbar-thumb {
  background: var(--yt-text-muted);
  border-radius: 2px;
}

.tags-container::-webkit-scrollbar-thumb:hover {
  background: var(--yt-text-secondary);
}

@media (max-width: 576px) {
  .selected-tags-list {
    flex-direction: column;
    align-items: flex-start;
  }

  .selected-tag-badge {
    font-size: 0.8rem;
    padding: 6px 10px;
  }
}
</style>